/** Feature keys from Admin Portal entitlements catalog (identity `ent` / product-access). */

export const PERMISSION_DENIED_MESSAGE = 'You do not have permission for that.';

export const AI_RANKING_OFF_MESSAGE =
  'Stack Ranking is turned off for your company. Candidates are listed with their basic details (name, email, phone) only — no scores or reports.';

export const AI_JD_OFF_MESSAGE =
  'AI JD generation is turned off for your company. You can still write, edit or upload a JD manually.';

export const REACHOUT_FEATURE_ROUTES = [
  // aiOnly: the feature switch only disables the AI inside these pages, never the pages —
  // every position starts from a JD, so manual create / edit / upload must always work.
  { feature: 'jd_craft', label: 'Craft JD', prefixes: ['/open-positions', '/jd-preview', '/database'], aiOnly: true },
  // Stack Ranking off = AI scoring/reports off; resume upload and the basic candidate list stay
  // open because outreach starts from that list.
  { feature: 'stack_ranking', label: 'Stack Ranking', prefixes: ['/stack-ranking', '/existing-positions'], aiOnly: true },
  { feature: 'outreach', label: 'ReachOut', prefixes: ['/engagement', '/whatsapp-reachout'] },
  { feature: 'resume_flow', label: 'Format Resume', prefixes: ['/resume-formatter'] },
];

function isOn(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function isProductEnabled(entitlements, productId, { unrestricted } = {}) {
  if (unrestricted) return true;
  if (!entitlements) return false;
  const bucket = entitlements[productId];
  return !!(bucket && isOn(bucket.enabled));
}

export function isFeatureEnabled(entitlements, productId, featureId, { unrestricted } = {}) {
  if (unrestricted) return true;
  if (!entitlements) return false;
  const bucket = entitlements[productId];
  if (!bucket || !isOn(bucket.enabled)) return false;
  if (!featureId) return true;
  return isOn(bucket.features?.[featureId]);
}

export function featureForPath(pathname) {
  const path = String(pathname || '');
  for (const row of REACHOUT_FEATURE_ROUTES) {
    if (row.prefixes.some((p) => path === p || path.startsWith(`${p}/`))) {
      return row;
    }
  }
  return null;
}

export function featureLabelForPath(pathname) {
  const row = featureForPath(pathname);
  if (row?.label) return row.label;
  const path = String(pathname || '');
  if (path === '/open-wizetok' || path.startsWith('/open-wizetok/') || path.startsWith('/sso/wizetok')) {
    return 'Wizetok';
  }
  return 'This feature';
}

/** True when this path is allowed for the current entitlements. */
export function isPathAllowed(pathname, entitlements, { unrestricted } = {}) {
  if (unrestricted) return true;
  const path = String(pathname || '');
  if (path === '/open-wizetok' || path.startsWith('/open-wizetok/')) {
    return isProductEnabled(entitlements, 'wizetok', { unrestricted });
  }
  const matches = REACHOUT_FEATURE_ROUTES.filter((row) =>
    row.prefixes.some((p) => path === p || path.startsWith(`${p}/`))
  );
  if (matches.length === 0) return true;
  return matches.some((row) => (row.aiOnly
    ? isProductEnabled(entitlements, 'reachout', { unrestricted })
    : isFeatureEnabled(entitlements, 'reachout', row.feature, { unrestricted })));
}

/** Craft JD's AI (generate / regenerate / AI formatting). Manual JD work never depends on this. */
export function isAiJdEnabled(entitlements, { unrestricted } = {}) {
  return isFeatureEnabled(entitlements, 'reachout', 'jd_craft', { unrestricted });
}

/** Stack Ranking's AI (scoring, reports, skill weightage generation). Upload and the basic list never depend on this. */
export function isAiRankingEnabled(entitlements, { unrestricted } = {}) {
  return isFeatureEnabled(entitlements, 'reachout', 'stack_ranking', { unrestricted });
}
